import React, { useCallback, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import type { CyberSweepBoardCardRow } from '../../services/queries/cyberSweepQueries';

export type CyberSweepBoardColumnProps = {
  colKey: string;
  label: string;
  icon: React.ReactNode;
  badgeCls: string;
  cardCls: string;
  items: CyberSweepBoardCardRow[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isInitialLoading: boolean;
  /** CSAudit hydration in flight for this column — show per-card spinners for missing phase (Credit Block–style). */
  phaseDetailsLoading?: boolean;
  /** Traversal count API total; when set, badge shows server total for the column. */
  columnTotalCount?: number;
  columnTotalCountPending?: boolean;
  onLoadMore: () => void;
  getPhaseStatusColor: (ps: string) => string;
  onClientClick: (clientName: string, auditId: string, sfId?: string) => void;
  onViewAudit: (auditId: string) => void;
};

/**
 * Single kanban column with scroll sentinel to load the next traversal page.
 */
export const CyberSweepBoardColumn: React.FC<CyberSweepBoardColumnProps> = ({
  colKey,
  label,
  icon,
  badgeCls,
  cardCls,
  items,
  hasNextPage,
  isFetchingNextPage,
  isInitialLoading,
  phaseDetailsLoading = false,
  columnTotalCount,
  columnTotalCountPending,
  onLoadMore,
  getPhaseStatusColor,
  onClientClick,
  onViewAudit,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) onLoadMore();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: el.parentElement ?? undefined, rootMargin: '80px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore, items.length, colKey]);

  const badgeText = (() => {
    if (typeof columnTotalCount === 'number') {
      return String(columnTotalCount);
    }
    if (columnTotalCountPending && items.length === 0 && isInitialLoading) {
      return '…';
    }
    return hasNextPage && items.length > 0 ? `${items.length}+` : String(items.length);
  })();

  return (
    <div className="w-64 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col">
      <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{label}</span>
          <span className={`ml-auto px-1.5 py-0.5 text-xs rounded-full font-medium ${badgeCls}`}>
            {badgeText}
          </span>
        </div>
      </div>
      <div className="p-2 space-y-2 overflow-y-auto flex-1" style={{ maxHeight: '60vh' }}>
        {isInitialLoading && items.length === 0 && (
          <div className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">Loading…</div>
        )}
        {!isInitialLoading && items.length === 0 && (
          <div className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">No records</div>
        )}
        {items.map((client) => (
          <div
            key={client.id}
            onClick={() => onClientClick(client.clientName, client.auditId, client.sfId)}
            className={`p-2.5 border rounded-lg cursor-pointer transition-colors ${cardCls}`}
          >
            <div className="font-medium text-sm text-gray-900 dark:text-gray-100 leading-tight">{client.clientName}</div>
            {client.phaseStatus ? (
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mt-1 ${getPhaseStatusColor(
                  client.phaseStatus
                )}`}
              >
                {client.phaseStatus}
              </span>
            ) : (
              phaseDetailsLoading && (
                <span className="inline-flex items-center gap-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-3 w-3 animate-spin shrink-0" aria-hidden />
                  <span>Phase…</span>
                </span>
              )
            )}
            {client.roadblock && <div className="text-xs text-red-600 mt-1 truncate">{client.roadblock}</div>}
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {client.auditor || client.dueDate || client.enrolledDate || ''}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewAudit(client.auditId);
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium flex-shrink-0 ml-1"
              >
                Audit
              </button>
            </div>
          </div>
        ))}
        {hasNextPage && <div ref={sentinelRef} className="h-4 w-full shrink-0" aria-hidden />}
        {isFetchingNextPage && (
          <div className="text-xs text-gray-400 text-center py-2">Loading more…</div>
        )}
      </div>
    </div>
  );
};
